import React from "react";
import axios from "axios";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { PlayerChip } from "@/components/PlayerChip";
import { HistoryTab } from "@/components/HistoryTab";
import { TargetsTab } from "@/components/TargetsTab";
import { GameTrendsTab } from "@/components/GameTrendsTab";
import { fmt, fmtPct, gameTimeLocal } from "@/lib/format";
import { MapPin, Crosshair, ShieldAlert, Zap, ChevronRight, History, TrendingUp, BadgeDollarSign } from "lucide-react";
import { AlignedStrip } from "@/components/AlignedAlerts";
import { PlayerName } from "@/components/PlayerCardDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const PERF_ROWS = [
  ["GF/GP", "gf_gp", fmt], ["GA/GP", "ga_gp", fmt], ["SF/GP", "sf_gp", (v) => fmt(v, 1)],
  ["SA/GP", "sa_gp", (v) => fmt(v, 1)], ["PPOF/GP", "ppof_gp", fmt], ["PP%", "pp_pct", fmtPct],
  ["PPOA/GP", "ppoa_gp", fmt], ["PK%", "pk_pct", fmtPct],
];

const TeamMark = ({ team, score, showScore }) => (
  <div className="flex items-center gap-2">
    {team.logo && <img src={team.logo} alt={team.abbrev} className="h-8 w-8 object-contain" loading="lazy" />}
    <div className="flex flex-col leading-none">
      <span className="font-display text-lg font-extrabold tracking-tight text-white">{team.abbrev || "?"}</span>
      {team.record && <span className="font-mono text-[10px] text-slate-500">{team.record}</span>}
    </div>
    {showScore && score != null && <span className="ml-1 font-mono text-2xl font-bold text-cyan-300 tabular-nums">{score}</span>}
  </div>
);

const SectionTitle = ({ icon: Icon, children, right }) => (
  <div className="mb-2 flex items-center justify-between border-b border-slate-800 pb-1.5">
    <span className="flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-widest text-slate-400">
      {Icon && <Icon className="h-3.5 w-3.5 text-cyan-500" />}
      {children}
    </span>
    {right}
  </div>
);

const TargetList = ({ label, targets, opponent, statsAvailable, lineupStatus, testId }) => {
  if (lineupStatus === "UNAVAILABLE") {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-slate-300">{label}</span>
          <StatusBadge status="UNAVAILABLE" size="xs" />
        </div>
        <p className="py-4 text-center font-mono text-[11px] text-slate-600">Lineup unavailable</p>
      </div>
    );
  }
  if (!statsAvailable) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-slate-300">{label}</span>
          <StatusBadge status="UNAVAILABLE" size="xs" />
        </div>
        <p className="py-4 text-center font-mono text-[11px] text-slate-600">Opponent stats unavailable</p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3" data-testid={testId}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-slate-300">{label}</span>
        <span className="font-mono text-[10px] text-slate-500">vs {opponent} weakness</span>
      </div>
      <div className="space-y-1.5">
        {targets.map((t, i) => (
          <div
            key={`${t.jersey}-${t.position}-${i}`}
            className={`flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 transition-colors ${
              t.is_primary
                ? "border-rose-800/60 bg-rose-950/30"
                : "border-slate-800/70 bg-slate-900/40 hover:bg-slate-900"
            }`}
          >
            <div className="flex min-w-0 items-center gap-2">
              <span className="w-9 shrink-0 text-right font-mono text-sm font-bold text-cyan-400 tabular-nums">
                {t.jersey != null ? `#${t.jersey}` : "#—"}
              </span>
              <span className="truncate font-display text-[13px] font-semibold uppercase tracking-tight text-slate-100">
                {t.name}
              </span>
              <span className="shrink-0 rounded bg-slate-800 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-slate-300">
                {t.position}
              </span>
              {t.line && <span className="shrink-0 font-mono text-[10px] text-slate-500">{t.line}</span>}
            </div>
            <div className="flex shrink-0 items-center gap-3 font-mono text-[11px]">
              <span className={`tabular-nums ${t.is_primary ? "text-rose-300 font-bold" : "text-amber-300"}`} title="Opponent GA/GP allowed to this position">
                {fmt(t.opp_ga_gp)} GA
              </span>
              <span className="tabular-nums text-slate-400" title="Opponent SOG allowed to this position">
                {fmt(t.opp_sog_allowed, 1)} SOG
              </span>
            </div>
          </div>
        ))}
        {targets.length === 0 && <p className="py-3 text-center font-mono text-[11px] text-slate-600">No matched players</p>}
      </div>
    </div>
  );
};

const PerfTable = ({ away, home, awayAbbr, homeAbbr }) => (
  <table className="w-full border-collapse">
    <thead>
      <tr>
        <th className="bg-slate-900/80 px-3 py-2 text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">Metric</th>
        <th className="bg-slate-900/80 px-3 py-2 text-right font-mono text-[10px] uppercase tracking-wider text-cyan-400">{awayAbbr}</th>
        <th className="bg-slate-900/80 px-3 py-2 text-right font-mono text-[10px] uppercase tracking-wider text-emerald-400">{homeAbbr}</th>
      </tr>
    </thead>
    <tbody>
      {PERF_ROWS.map(([label, key, f]) => (
        <tr key={key} className="border-t border-slate-800/60">
          <td className="px-3 py-1.5 font-mono text-[11px] font-medium text-slate-400">{label}</td>
          <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-200 tabular-nums">{away ? f(away[key]) : "—"}</td>
          <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-200 tabular-nums">{home ? f(home[key]) : "—"}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const PositionTable = ({ rows, awayAbbr, homeAbbr, unit, heat }) => (
  <table className="w-full border-collapse">
    <thead>
      <tr>
        <th className="bg-slate-900/80 px-3 py-2 text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">Pos</th>
        <th className="bg-slate-900/80 px-3 py-2 text-right font-mono text-[10px] uppercase tracking-wider text-cyan-400">{awayAbbr} {unit}</th>
        <th className="bg-slate-900/80 px-3 py-2 text-right font-mono text-[10px] uppercase tracking-wider text-emerald-400">{homeAbbr} {unit}</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.position} className="border-t border-slate-800/60">
          <td className="px-3 py-1.5 font-mono text-[11px] font-bold text-slate-300">{r.position}</td>
          <td className={`px-3 py-1.5 text-right font-mono text-xs tabular-nums ${heat && r.away_is_top ? "font-bold text-rose-300" : "text-slate-200"}`}>{fmt(r.away, heat ? 2 : 1)}</td>
          <td className={`px-3 py-1.5 text-right font-mono text-xs tabular-nums ${heat && r.home_is_top ? "font-bold text-rose-300" : "text-slate-200"}`}>{fmt(r.home, heat ? 2 : 1)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const LineupColumn = ({ team, lineup, status, testId }) => {
  if (status === "UNAVAILABLE" || !lineup) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3" data-testid={testId}>
        <div className="mb-2 flex items-center justify-between">
          <span className="font-display text-sm font-bold uppercase text-white">{team.abbrev}</span>
          <StatusBadge status="UNAVAILABLE" size="xs" />
        </div>
        <p className="py-6 text-center font-mono text-[11px] text-slate-600">Lineup unavailable</p>
      </div>
    );
  }
  const stChips = (arr) => (arr || []).map((p, i) => (
    <PlayerChip key={`${p.jersey}-${i}`} jersey={p.jersey} name={p.name} team={team.abbrev} position={p.position} verified={p.verified} injury={p.injury} />
  ));
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3" data-testid={testId}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {team.logo && <img src={team.logo} alt="" className="h-5 w-5 object-contain" />}
          <span className="font-display text-sm font-bold uppercase text-white">{team.abbrev}</span>
        </div>
        <StatusBadge status={status} size="xs" />
      </div>
      {lineup.source_name && (
        <p className="mb-2 font-mono text-[9px] uppercase tracking-wider text-slate-600">Src: {lineup.source_name}</p>
      )}

      <p className="mb-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-cyan-600">Forward Lines</p>
      <div className="space-y-2.5">
        {lineup.forward_lines.map((l) => (
          <div key={l.line}>
            <p className="mb-1 font-mono text-[9px] uppercase text-slate-600">Line {l.line}</p>
            <div className="space-y-1">
              {l.players.map((p, i) => (
                <PlayerChip key={`${p.jersey}-${i}`} jersey={p.jersey} name={p.name} team={team.abbrev} position={p.position} line={`L${l.line}`} verified={p.verified} injury={p.injury} />
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="mb-1.5 mt-3 font-mono text-[10px] font-semibold uppercase tracking-widest text-cyan-600">Defense Pairings</p>
      <div className="space-y-2.5">
        {lineup.defense_pairs.map((pr) => (
          <div key={pr.pair}>
            <p className="mb-1 font-mono text-[9px] uppercase text-slate-600">Pair {pr.pair}</p>
            <div className="space-y-1">
              {pr.players.map((p, i) => (
                <PlayerChip key={`${p.jersey}-${i}`} jersey={p.jersey} name={p.name} team={team.abbrev} position={p.position} line={`P${pr.pair}`} verified={p.verified} injury={p.injury} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {["pp1", "pp2", "pk1", "pk2"].some((k) => (lineup.special_teams[k] || []).length) && (
        <>
          <p className="mb-1.5 mt-3 font-mono text-[10px] font-semibold uppercase tracking-widest text-amber-600">Special Teams</p>
          <div className="space-y-2.5">
            {[["1st Powerplay (PP1)", "pp1"], ["2nd Powerplay (PP2)", "pp2"], ["1st Penalty Kill (PK1)", "pk1"], ["2nd Penalty Kill (PK2)", "pk2"]].map(([lbl, key]) => (
              (lineup.special_teams[key] || []).length > 0 && (
                <div key={key}>
                  <p className="mb-1 font-mono text-[9px] uppercase text-slate-600">{lbl}</p>
                  <div className="space-y-1">{stChips(lineup.special_teams[key])}</div>
                </div>
              )
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const SubTabs = ({ items, testKey }) => (
  <div className="px-4 pt-2">
    <TabsList className="h-10 bg-transparent p-0">
      {items.map(([v, I, l]) => (
        <TabsTrigger key={v} value={v} data-testid={`tab-${v}-${testKey}`} className="h-9 rounded-full px-3 font-mono text-[11px] uppercase tracking-wider text-slate-500 data-[state=active]:bg-slate-800 data-[state=active]:text-cyan-300">
          <I className="mr-1 h-3.5 w-3.5" /> {l}
        </TabsTrigger>
      ))}
    </TabsList>
  </div>
);

const CardSummary = ({ s, testKey }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-800/60 px-4 py-2 pl-11 font-mono text-[10px] text-slate-400" data-testid={`card-summary-${testKey}`}>
    {(s.goalies || []).filter(Boolean).length > 0 && (
      <span>🥅 {(s.goalies || []).filter(Boolean).map((g) => `${g.name} (${g.status === "CONFIRMED" ? "conf" : "proj"})`).join(" vs ")}</span>
    )}
    {s.top && <span>🎯 Top: <span className="text-slate-200">{s.top.player}</span> {s.top.team} {s.top.slot} · <span className="text-emerald-300">{s.top.score}</span></span>}
    {s.value && <span>💲 Value: <span className="text-slate-200">{s.value.name}</span> <span className="text-emerald-300">{s.value.best}</span> (+{s.value.edge})</span>}
  </div>
);

const GameOdds = ({ analysis, loading, testKey }) => {
  if (loading || !analysis) return <p className="py-6 text-center font-mono text-[11px] text-slate-500">Loading odds…</p>;
  const cy = analysis.cypher || {};
  const rows = ["away", "home"].flatMap((k) => (cy[k]?.targets || []).filter((t) => t.atg).map((t) => ({ ...t, team: cy[k].attacker })))
    .sort((a, b) => b.atg.implied - a.atg.implied);
  if (!rows.length) return <p className="py-6 text-center font-mono text-[11px] text-slate-600">No anytime goal lines posted for this game yet</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-800" data-testid={`game-odds-${testKey}`}>
      <table className="w-full font-mono text-[11px]">
        <thead className="bg-slate-900 text-[9px] uppercase tracking-wider text-slate-500">
          <tr><th className="px-2 py-2 text-left">Player</th><th className="px-2 py-2 text-left">Slot</th><th className="px-2 py-2 text-right">Best</th><th className="px-2 py-2 text-right">Impl %</th><th className="px-2 py-2 text-right">Model %</th><th className="px-2 py-2 text-right">Edge</th></tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={`${t.team}-${t.jersey}`} className={`border-t border-slate-800/60 ${t.atg.value ? "bg-emerald-950/15" : ""}`} title={Object.entries(t.atg.books || {}).map(([b, o]) => `${b} ${o}`).join(" · ")}>
              <td className="whitespace-nowrap px-2 py-2"><PlayerName name={t.player} team={t.team} className="font-bold text-slate-100" /> <span className="text-slate-500">{t.team}</span>{t.atg.value && <span className="ml-1.5 rounded bg-emerald-600 px-1 text-[8px] font-bold text-white">VALUE</span>}</td>
              <td className="px-2 py-2 text-slate-400">{t.slot}</td>
              <td className="whitespace-nowrap px-2 py-2 text-right font-bold text-emerald-300">{t.atg.best} <span className="text-[9px] font-normal text-slate-500">{t.atg.best_book}</span></td>
              <td className="px-2 py-2 text-right">{t.atg.implied}</td>
              <td className="px-2 py-2 text-right text-cyan-300">{t.atg.model ?? "—"}</td>
              <td className={`px-2 py-2 text-right font-bold ${t.atg.edge >= 3 ? "text-emerald-300" : t.atg.edge < 0 ? "text-rose-400" : "text-slate-300"}`}>{t.atg.edge > 0 ? "+" : ""}{t.atg.edge}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const GameCard = ({ game, index, isOpen, onToggle, summary }) => {
  const { away, home, statuses, away_stats, home_stats, positional, away_lineup, home_lineup } = game;
  const showScore = game.status_state === "in" || game.status_state === "post";
  const testKey = `${(away.abbrev || "away").toLowerCase()}-${(home.abbrev || "home").toLowerCase()}`;

  const [analysis, setAnalysis] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!away.abbrev || !home.abbrev) { setErr(true); return; }
    setLoading(true);
    setErr(false);
    try {
      const res = await axios.get(`${API}/game-analysis`, { params: { away: away.abbrev, home: home.abbrev, games: 10 } });
      setAnalysis(res.data);
    } catch (e) {
      console.error(e);
      setErr(true);
    } finally {
      setLoading(false);
    }
  }, [away.abbrev, home.abbrev]);

  React.useEffect(() => {
    if (isOpen && !analysis && !loading && !err) load();
  }, [isOpen, analysis, loading, err, load]);

  return (
    <div
      data-testid={`game-card-${testKey}`}
      className="fade-up overflow-hidden rounded-xl border border-slate-800 bg-[#0B0F17] shadow-[0_0_20px_rgba(0,0,0,0.6)] transition-all duration-300 hover:border-cyan-900/60"
      style={{ animationDelay: `${Math.min(index * 40, 320)}ms` }}
    >
      {/* clickable header */}
      <button
        data-testid={`game-toggle-${testKey}`}
        onClick={onToggle}
        className={`flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left transition-colors ${isOpen ? "border-b border-slate-800 bg-gradient-to-r from-cyan-950/30 to-transparent" : "hover:bg-slate-900/40"}`}
      >
        <div className="flex items-center gap-3">
          <ChevronRight className={`h-4 w-4 shrink-0 text-cyan-400 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
          <TeamMark team={away} score={away.score} showScore={showScore} />
          <span className="font-mono text-xs font-bold text-slate-600">@</span>
          <TeamMark team={home} score={home.score} showScore={showScore} />
        </div>
        <div className="flex items-center gap-3">
          {game.venue && (
            <span className="hidden items-center gap-1.5 font-mono text-[10px] text-slate-500 md:flex">
              <MapPin className="h-3 w-3" /> {game.venue}
            </span>
          )}
          <span className="font-mono text-xs font-semibold text-slate-300">{gameTimeLocal(game.date_utc, game.status_state, game.status_detail)}</span>
          <StatusBadge status={statuses.espn} label={statuses.espn === "SCHEDULED" ? "ESPN LIVE" : statuses.espn} testId={`game-status-${testKey}`} />
          <span className={`hidden items-center gap-1 rounded-full border px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest transition-all sm:flex ${isOpen ? "border-cyan-500/50 bg-cyan-500/15 text-cyan-300" : "border-slate-700 bg-slate-900/60 text-slate-400"}`}>
            {isOpen ? "Close" : "View Targets"} <span className={`transition-transform ${isOpen ? "rotate-90" : ""}`}>→</span>
          </span>
        </div>
      </button>
      {!isOpen && summary && <CardSummary s={summary} testKey={testKey} />}
      <AlignedStrip rows={summary?.aligned} testKey={testKey} />

      {isOpen && (
        <>
          <Tabs defaultValue="picks" className="w-full">
            <div className="px-4 pt-2">
              <TabsList className="grid h-11 w-full grid-cols-3 bg-slate-900/70">
                {[["picks", Crosshair, "Picks"], ["matchup", ShieldAlert, "Matchup"], ["odds", BadgeDollarSign, "Odds"]].map(([v, I, l]) => (
                  <TabsTrigger key={v} value={v} data-testid={`tab-group-${v}-${testKey}`} className="h-9 font-mono text-[12px] uppercase tracking-wider data-[state=active]:bg-cyan-950 data-[state=active]:text-cyan-300">
                    <I className="mr-1.5 h-4 w-4" /> {l}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            <TabsContent value="picks" className="mt-0">
            <Tabs defaultValue="trends">
              <SubTabs testKey={testKey} items={[["trends", TrendingUp, "Game Trends"], ["targets", Crosshair, "Goal Origin"]]} />
            {/* GAME TRENDS */}
            <TabsContent value="trends" className="px-4 pb-4 pt-2">
              <GameTrendsTab
                analysis={analysis?.analysis}
                history={analysis?.history}
                game={game}
                loading={loading}
                error={err}
                onRetry={load}
              />
            </TabsContent>

            {/* GOAL ORIGIN */}
            <TabsContent value="targets" className="px-4 pb-4 pt-2">
              <TargetsTab
                analysis={analysis?.analysis}
                loading={loading}
                error={err}
                awayAbbr={away.abbrev}
                homeAbbr={home.abbrev}
                onRetry={load}
              />
            </TabsContent>

            </Tabs>
            </TabsContent>

            <TabsContent value="matchup" className="mt-0">
            <Tabs defaultValue="matchup">
              <SubTabs testKey={testKey} items={[["matchup", ShieldAlert, "Matchup"], ["lineups", Zap, "Lineups"], ["history", History, "History"]]} />
            {/* MATCHUP */}
            <TabsContent value="matchup" className="space-y-4 px-4 pb-4 pt-2">
              <div>
                <SectionTitle right={<StatusBadge status={away_stats && home_stats ? "UPDATED" : "UNAVAILABLE"} size="xs" />}>Team Performance</SectionTitle>
                {away_stats || home_stats ? (
                  <div className="overflow-hidden rounded-lg border border-slate-800" data-testid={`perf-table-${testKey}`}>
                    <PerfTable away={away_stats} home={home_stats} awayAbbr={away.abbrev} homeAbbr={home.abbrev} />
                  </div>
                ) : (
                  <p className="py-3 text-center font-mono text-[11px] text-slate-600">Team data unavailable</p>
                )}
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <SectionTitle>Positional Weakness · GA/GP</SectionTitle>
                  <div className="overflow-hidden rounded-lg border border-slate-800" data-testid={`pos-weakness-${testKey}`}>
                    <PositionTable rows={positional.ga} awayAbbr={away.abbrev} homeAbbr={home.abbrev} unit="GA" heat />
                  </div>
                </div>
                <div>
                  <SectionTitle>SOG Allowed By Position</SectionTitle>
                  <div className="overflow-hidden rounded-lg border border-slate-800" data-testid={`sog-allowed-${testKey}`}>
                    <PositionTable rows={positional.sog} awayAbbr={away.abbrev} homeAbbr={home.abbrev} unit="SOG" />
                  </div>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="history" className="px-4 pb-4 pt-2">
              <SectionTitle icon={History} right={<span className="font-mono text-[9px] uppercase tracking-wider text-slate-600">Last 5 / 10</span>}>
                Historical Goal-Position Engine
              </SectionTitle>
              <HistoryTab loading={loading} error={err} history={analysis?.history} game={game} goalieOrigin={analysis?.analysis?.goalie_origin} onRetry={load} />
            </TabsContent>

            {/* LINEUPS */}
            <TabsContent value="lineups" className="px-4 pb-4 pt-2">
              <SectionTitle icon={Zap}>Lineups · Forwards · Defense · Special Teams</SectionTitle>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <LineupColumn team={away} lineup={away_lineup} status={statuses.away_lineup} testId={`lineup-away-${testKey}`} />
                <LineupColumn team={home} lineup={home_lineup} status={statuses.home_lineup} testId={`lineup-home-${testKey}`} />
              </div>
            </TabsContent>
            </Tabs>
            </TabsContent>

            <TabsContent value="odds" className="px-4 pb-4 pt-2">
              <GameOdds analysis={analysis?.analysis} loading={loading} testKey={testKey} />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default GameCard;
